/* FOM · lentes do mapa (Fase 1S).

   ATUAL · FUTURA · COMPLETA · MUDANCAS — quatro leituras do MESMO dado.

   O criterio de "futuro" NAO foi inventado aqui: e o que o proprio dominio ja
   declara. O backend deriva a camada na criacao —
     layer ?? (st === 'TOBE' ? 'tobe' : 'asis')
   (nodes/routes.ts) — e a classificacao TOBE existe desde a BASE congelada:
   10 nos da BASE carregam st='TOBE' hoje. Portanto:

     FUTURO(no) = layer === 'tobe'  OU  st === 'TOBE'

   Nada alem disso e derivavel com seguranca:
     · "elemento ALTERADO" (o no atual que um no TO-BE substituiria) nao tem
       representacao no dominio — nao existe vinculo entre um TO-BE e o seu
       correspondente atual. Fica REGISTRADO como pendencia, nao improvisado;
     · relacao nao tem camada propria: ela e futura quando TOCA um no futuro.

   As lentes sao LEITURA: nenhuma muda dado, nenhuma re-renderiza o mapa.
   Quem esconde/realca e o CSS, por atributo — zoom, pan e selecao ficam
   exatamente onde estavam. */

export type Lente = 'atual' | 'futura' | 'completa' | 'mudancas';

export const LENTES: { id: Lente; rotulo: string; dica: string }[] = [
  { id: 'atual', rotulo: 'Atual', dica: 'o que esta mapeado como atual (AS-IS)' },
  { id: 'futura', rotulo: 'Futura', dica: 'o futuro declarado (TO-BE) — intencao, nao operacao implantada' },
  { id: 'completa', rotulo: 'Completa', dica: 'tudo: o atual consolidado e o futuro diferenciado' },
  { id: 'mudancas', rotulo: 'Mudancas', dica: 'o que muda entre o que temos e o que queremos' },
];

type NoLente = { layer: string | null; st: string };

export function ehFuturo(no: NoLente): boolean {
  return no.layer === 'tobe' || no.st === 'TOBE';
}

export type ContagensLente = {
  atual: number;
  futura: number;
  completa: number;
  mudancas: number;
};

/* Contagens REAIS sobre os nos carregados — o seletor mostra exatamente o que
   cada lente vai exibir. */
export function contarPorLente(nos: NoLente[]): ContagensLente {
  const futura = nos.filter(ehFuturo).length;
  return {
    atual: nos.length - futura,
    futura,
    completa: nos.length,
    /* MUDANCAS = os elementos futuros (novos) + o que os toca. A contagem do
       seletor e dos NOS novos; relacoes e lacunas aparecem na propria lente. */
    mudancas: futura,
  };
}

/* ------------------------- contadores da visao (Fase 1X.1 · P0-1) --------
   REGRA: um numero apresentado como resumo de uma visao e derivado do MESMO
   conjunto semantico que a visao renderiza — nunca de totais fisicos do
   banco. Definicoes (documentadas em FOM-ARQ-0032):
     ATUAL     nos carregados que nao sao futuro
     FUTURA    nos futuros (ehFuturo)
     COMPLETA  todos os nos carregados (visiveis)
     MUDANCAS  o mesmo conjunto da FUTURA (novos elementos declarados)
     BASE/USER proveniencia (origin) — filtro ortogonal as lentes
     OCULTOS   removed_at != null — NUNCA entram em contador de visao; o
               total fisico vive apenas na aba Ocultos
   Conhecimentos e lacunas seguem o NO a que pertencem: conhecimento de no
   fora da visao (oculto ou fora da lente) nao conta na leitura da visao.
   Nada aqui apaga ou reescreve dado — e leitura. */

export type ContagensVisao = {
  elementos: number;
  relacoes: number;
  conhecimentos: number;
  lacunas: number;
};

type NoContavel = NoLente & {
  external_id: string;
  removed_at?: string | null;
  know: unknown[];
  gaps: unknown[];
};

export function contagensDaVisao(
  nos: NoContavel[],
  relacoes: { from: string; to: string }[],
  lente: Lente,
): ContagensVisao {
  /* defesa em profundidade: a API ja nao entrega ocultos, mas o contador
     jamais pode depender disso */
  const visiveis = nos.filter((n) => !n.removed_at);
  const daLente = visiveis.filter((n) =>
    lente === 'completa' ? true : lente === 'atual' ? !ehFuturo(n) : ehFuturo(n));
  const ids = new Set(daLente.map((n) => n.external_id));
  const idsVisiveis = new Set(visiveis.map((n) => n.external_id));
  /* relacao pertence a visao quando os DOIS lados sao visiveis e, na lente,
     quando ela toca o conjunto da lente (mesma regra do realce visual) */
  const rels = relacoes.filter((r) => idsVisiveis.has(r.from) && idsVisiveis.has(r.to))
    .filter((r) => (lente === 'completa'
      ? true
      : lente === 'atual'
        ? ids.has(r.from) && ids.has(r.to)
        : ids.has(r.from) || ids.has(r.to)));
  return {
    elementos: daLente.length,
    relacoes: rels.length,
    conhecimentos: daLente.reduce((s, n) => s + n.know.length, 0),
    lacunas: daLente.reduce((s, n) => s + n.gaps.length, 0),
  };
}

/* Para as lentes FUTURA e MUDANCAS o layout COMPACTA: os irmaos atuais nao
   ocupam espaco (fantasma nao empurra parede). Ficam os elementos futuros e a
   cadeia de ancestrais — o esqueleto que diz ONDE o futuro se pendura. E
   filtro de LAYOUT, nao de dado: contagens, painel e busca seguem vendo tudo. */
export function apenasFuturoEAncestrais<T extends NoLente & { external_id: string; parent_external_id: string | null }>(
  nos: T[],
): T[] {
  const porId = new Map(nos.map((n) => [n.external_id, n]));
  const manter = new Set<string>();
  for (const n of nos) {
    if (!ehFuturo(n)) continue;
    let atual: T | undefined = n;
    let guarda = 0;
    while (atual && guarda++ < 64 && !manter.has(atual.external_id)) {
      manter.add(atual.external_id);
      atual = atual.parent_external_id ? porId.get(atual.parent_external_id) : undefined;
    }
  }
  return nos.filter((n) => manter.has(n.external_id));
}
